import { Box, Icon } from "@outschool/backpack";
import { centsPerLearnerPerMeeting } from "@outschool/business-rules";
import { faBell, fasBell } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import {
  sectionCharterSchoolEnrollPath,
  sectionEnrollPath,
  sectionWaitlistPath,
  selfPacedEnrollPath
} from "@outschool/routes";
import {
  AuthTrigger,
  useLoginFlowContext,
  useSession
} from "@outschool/ui-auth";
import {
  TrackedButton,
  TrackedButtonProps
} from "@outschool/ui-components-shared";
import {
  JoinSubscriptionModal,
  JoinSubscriptionModalTrackingEventSource,
  SectionCallToActionProps,
  useEnrollButtonCTA,
  useRequiresMembershipForEnrollment,
  useSubscriptionCredit
} from "@outschool/ui-components-website";
import { useNavigation } from "@outschool/ui-utils";
import React from "react";
import { useHistory } from "react-router";
import { useLocation } from "react-router-dom";

import * as Activity from "../../../shared/Activity";
import * as Section from "../../../shared/Section";
import { useWaitlistSeatsForParentQuery } from "../../queries/WaitlistQuery";

export function SectionCallToAction({
  activity,
  section,
  isOrganizationEnrollment,
  size,
  parentUid,
  variant = "contained",
  disabled = false,
  trackingSharedProperties = {},
  sx
}: SectionCallToActionProps) {
  const isSelfPaced = activity.isSelfPaced;

  const isWaitlistable =
    !isSelfPaced && Section.isWaitlistable(activity, section);

  const { waitlistSeats, loading } = useWaitlistSeatsForParentQuery({
    parentUid,
    sectionUid: section?.uid,
    skip: !section
  });

  const showWaitlistedButton =
    !isSelfPaced && waitlistSeats?.length > 0 && Section.isFull(section);

  const showNotAcceptingWaitlist =
    !isSelfPaced && Section.isNotAcceptingWaitlistSeats(activity, section);

  const ctaProperties = {
    isOngoing: !!activity.is_ongoing_weekly,
    isSelfPaced,
    isClub: activity.isClub,
    isInProgress: Section.isInProgress(section),
    isWaitlistable,
    sectionProgress: section?.progress,
    showWaitlistedButton,
    showNotAcceptingWaitlist,
    canEnroll: activity.canEnroll
  };

  const cta = useEnrollButtonCTA(ctaProperties);

  if (loading) {
    return null;
  }

  const trackingProperties = {
    ...trackingSharedProperties,
    ...ctaProperties,
    activity_uid: activity?.uid,
    section_uid: section?.uid,
    cta
  };

  if (section && isOrganizationEnrollment) {
    return (
      <SectionOrganizationCallToAction
        activity={activity}
        section={section}
        size={size}
        variant={variant}
        disabled={
          disabled || isWaitlistable || (isSelfPaced && !activity.canEnroll)
        }
        trackingSharedProperties={trackingProperties}
        sx={sx}
        isWaitlistable={isWaitlistable}
      />
    );
  }

  if (
    section?.isBuyable ||
    isWaitlistable ||
    showNotAcceptingWaitlist ||
    activity.isSelfPaced
  ) {
    return (
      <SectionEnrollButton
        activity={activity}
        section={section}
        size={size}
        variant={variant}
        cta={cta}
        disabled={disabled}
        isWaitlistable={isWaitlistable}
        isSelfPaced={isSelfPaced}
        showWaitlistedButton={showWaitlistedButton}
        showNotAcceptingWaitlist={showNotAcceptingWaitlist}
        trackingSharedProperties={trackingProperties}
        sx={sx}
      />
    );
  }

  return null;
}

function SectionOrganizationCallToAction({
  activity,
  section,
  size,
  variant,
  disabled,
  trackingSharedProperties = {},
  sx,
  isWaitlistable
}: $TSFixMe) {
  const { t } = useTranslation(
    "client\\components\\detailPage\\SectionCallToActionButtons"
  );
  const history = useHistory();

  const path = sectionCharterSchoolEnrollPath(activity, section);
  const cta = isWaitlistable ? t`Section Full` : t`Admin Enroll`;

  return (
    <SectionCallToActionButton
      onClick={event => {
        event.stopPropagation();
        history.push(path);
      }}
      size={size}
      variant={variant}
      disabled={disabled}
      trackingSharedProperties={trackingSharedProperties}
      sx={sx}
    >
      {cta}
    </SectionCallToActionButton>
  );
}

function SectionEnrollButton({
  activity,
  section,
  cta,
  size,
  variant,
  disabled,
  isWaitlistable = false,
  showWaitlistedButton = false,
  showNotAcceptingWaitlist = false,
  trackingSharedProperties = {},
  isSelfPaced = false,
  sx
}: $TSFixMe) {
  const history = useHistory();
  const path = isSelfPaced
    ? selfPacedEnrollPath(activity)
    : isWaitlistable || showWaitlistedButton
    ? sectionWaitlistPath(activity, section)
    : sectionEnrollPath(activity, section);

  const [isHovering, setIsHovering] = React.useState(false);
  const [showJoinSubscriptionModal, setShowJoinSubscriptionModal] =
    React.useState(false);

  const { isLoggedIn } = useSession();
  const { enterLoginFlow } = useLoginFlowContext();

  const {
    joinSubscriptionModalEnabled,
    setSeenJoinSubscriptionModal,
    hasSubscription,
    canPayWithSubscriptionCredits
  } = useSubscriptionCredit();
  const location = useLocation();
  const navigate = useNavigation();

  const { requiresMembershipForEnrollment } =
    useRequiresMembershipForEnrollment();

  // NOTE: While the LoggedOutMembershipsExperiment is running, hide the membership upsell.
  const joinSubscriptionModalEnabledOverride =
    joinSubscriptionModalEnabled && !requiresMembershipForEnrollment;

  const onClickCallback = React.useCallback(
    (event?: any) => {
      event?.stopPropagation();
      const [basePath, _query] = path.split("/enroll");
      if (isLoggedIn) {
        if (
          requiresMembershipForEnrollment &&
          (!hasSubscription || !canPayWithSubscriptionCredits)
        ) {
          const queryParams = new URLSearchParams(location.search);
          // eslint-disable-next-line i18next/no-literal-string
          queryParams.set("joinMembershipModal", "true");
          // eslint-disable-next-line i18next/no-literal-string
          queryParams.set("redirectAfterPath", path);
          navigate(`${location.pathname}?${queryParams.toString()}`, {
            replace: true
          });
        } else {
          history.push(path);
        }
      } else {
        enterLoginFlow({
          authTrigger: requiresMembershipForEnrollment
            ? AuthTrigger.JOIN_OUTSCHOOL
            : AuthTrigger.ENROLL,
          skipLearners: true,
          newUrl: requiresMembershipForEnrollment
            ? basePath +
              `?joinMembershipModal=true&redirectAfterPath=${encodeURIComponent(
                path
              )}`
            : path
        });
      }
    },
    [
      isLoggedIn,
      requiresMembershipForEnrollment,
      location.search,
      location.pathname,
      navigate,
      history,
      path,
      enterLoginFlow,
      hasSubscription,
      canPayWithSubscriptionCredits
    ]
  );

  const onClickJoinSubscription = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setShowJoinSubscriptionModal(true);
      setSeenJoinSubscriptionModal();
    },
    [setSeenJoinSubscriptionModal]
  );

  let otherProps: any = {};

  if (showWaitlistedButton && !showNotAcceptingWaitlist) {
    otherProps.startIcon = (
      <Icon color={isHovering ? "inherit" : "primary"} icon={fasBell} />
    );
    otherProps.sx = { backgroundColor: "primary.100", color: "black" };
  } else if (isWaitlistable) {
    otherProps.startIcon = <Icon icon={faBell} />;
  }

  return (
    activity &&
    (section || isSelfPaced) && (
      <Box>
        <SectionCallToActionButton
          onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            event.stopPropagation();
            if (joinSubscriptionModalEnabledOverride) {
              onClickJoinSubscription(event);
            } else {
              onClickCallback(event);
            }
          }}
          disabled={
            showNotAcceptingWaitlist ||
            disabled ||
            (isSelfPaced && !activity.canEnroll)
          }
          size={size}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          {...otherProps}
          variant={variant}
          trackingName={
            isWaitlistable
              ? Activity.ACTIVITY_WAITLIST_CLICK
              : Activity.ACTIVITY_ENROLL_CLICK
          }
          trackingSharedProperties={trackingSharedProperties}
          trackingTouchEventName={
            isWaitlistable
              ? Activity.ACTIVITY_WAITLIST_CLICK
              : Activity.ACTIVITY_ENROLL_CLICK
          }
          trackingUniqueId={section?.uid || activity.uid}
          sx={{ ...sx, ...otherProps.sx }}
        >
          {cta}
        </SectionCallToActionButton>
        <JoinSubscriptionModal
          isOpen={showJoinSubscriptionModal}
          onClose={() => {
            setShowJoinSubscriptionModal(false);
            onClickCallback();
          }}
          priceInCents={centsPerLearnerPerMeeting(activity)}
          proceedToEnroll={(e: React.MouseEvent<HTMLElement>) => {
            setShowJoinSubscriptionModal(false);
            onClickCallback(e);
          }}
          redirectAfterPathOverride={path}
          eventSource={JoinSubscriptionModalTrackingEventSource.SectionEnroll}
        />
      </Box>
    )
  );
}

export function SectionCallToActionButton({ ...props }: TrackedButtonProps) {
  const onClick = (event: $TSFixMe) => {
    event.stopPropagation();
  };
  return (
    <TrackedButton
      onClick={onClick}
      data-test-id="SectionCallToActionButton"
      {...props}
      trackingSharedProperties={{
        is_auto_scheduled_section: false,
        ...props.trackingSharedProperties
      }}
    />
  );
}
